/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2025-02-14 11:32:37
 */
/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react'
import type { ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout,
  Table,
  Button,
  Tooltip,
  message,
  Upload,
  Divider,
  Modal,
  Input
} from 'antd'
import type { UploadFile, UploadProps } from 'antd'
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import Excel from 'exceljs'
import FileSaver from 'file-saver'
import { bunchInvite } from '../../actions/team'
import { fetchGroupInfo, fetchGroupDeparment } from '../../actions/group'
import sensors from '../../utils/sensors'
import CONFIG from '../../constants/config'
import parseQueryString from '../../utils/parseQueryString'
import excelIcon from '../../images/excel_icon.jpeg'
import { capturePromiseException } from '../../utils/sentryUtils'
import { loadScript } from '../../utils/utils'
import { useAppDispatch, useAppSelector } from '../../app/hooks'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const { Column } = Table
const { TextArea } = Input
const { Dragger } = Upload

// 腾讯验证码实例类型
interface TencentCaptchaInstance {
  show: () => void
}

// 腾讯验证码回调结果
interface TencentCaptchaResult {
  ret: number
  ticket: string
  randstr: string
}

// 成员数据类型
interface MemberData {
  key: number
  userName: string
  userMobile: string
}

// 失败的手机号数据
interface FailedMobile {
  mobileStatus: number
  userMobile: string
}

// API 响应数据
interface ImportResponse {
  status: number
  msg?: string
  mobiles?: FailedMobile[]
}

// 组件 Props 类型
interface ImportFromInputProps {
  errorUsers: MemberData[]
  onImport: (
    users: MemberData[],
    res: TencentCaptchaResult
  ) => Promise<ImportResponse>
  onSuccess: () => void
}

interface ImportFromExcelProps {
  onImport: (
    users: MemberData[],
    res: TencentCaptchaResult
  ) => Promise<ImportResponse>
  onSuccess: () => void
  onError: (errorUsers: MemberData[]) => void
}

// 声明全局 TencentCaptcha
declare const TencentCaptcha: new (
  appId: string,
  callback: (res: TencentCaptchaResult) => void
) => TencentCaptchaInstance

let tencentCaptcha: TencentCaptchaInstance | null = null

const handleReasons = (failedMobiles: FailedMobile[]) => {
  const reasons = {
    isExist: 0, // 已在团队中
    isDuplicate: 0, // 24小时内已邀请
    sendMsmFail: 0, // 发送短信失败
    mobileError: 0, // 手机号不正确
    other: 0 // 其他,请联系客服
  }
  failedMobiles.forEach((mobile) => {
    if (mobile.mobileStatus === 4) {
      reasons.isExist++
    } else if (mobile.mobileStatus === 7) {
      reasons.isDuplicate++
    } else if (mobile.mobileStatus === -8) {
      reasons.sendMsmFail++
    } else if (mobile.mobileStatus === -18) {
      reasons.mobileError++
    } else {
      reasons.other++
    }
  })
  return reasons
}

const ImportFromInput: React.FC<ImportFromInputProps> = ({
  errorUsers,
  onImport,
  onSuccess
}) => {
  const [value, setValue] = useState('')
  const [members, setMembers] = useState<MemberData[]>([])
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const lines = errorUsers
      .map((user) => `${user.userName} ${user.userMobile}`)
      .join('\n')
    setMembers(errorUsers)
    setValue(lines)
    setHasError(
      errorUsers.some((user) => !CONFIG.mobileRegex.test(user.userMobile))
    )
  }, [errorUsers])

  const splitInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const cleanValue = e.target.value
      .replace(/\t/g, ' ')
      .replace(/,/g, ' ')
      .replace(/，/g, ' ')
    setValue(cleanValue)
    const lines = cleanValue.split('\n')
    const users = parseLinesToUsers(lines)
    setHasError(users.some((user) => !CONFIG.mobileRegex.test(user.userMobile)))
    setMembers(users)
  }

  const parseLineToNameAndMobile = useCallback(
    (line: string): string[] =>
      line
        .trim()
        .split(' ')
        .filter((item) => !!item),
    []
  )

  const parseLinesToUsers = useCallback(
    (lines: string[]): MemberData[] => {
      const users = lines.map((line, index) => {
        const nameAndMobile = parseLineToNameAndMobile(line)
        return {
          key: index,
          userName: nameAndMobile[0],
          userMobile: nameAndMobile[1]
        }
      })
      return users
    },
    [parseLineToNameAndMobile]
  )

  /**
   *
   * {
      "code": 200,
      "msg": "success",
      "toast_msg": "",
      "data": {
        "status": 0, // 外层状态 0 成功，非0失败，显示msg
        "mobiles": [
          {
            "mobileStatus": 0, // 个人状态 0成功 4已在团队中 7重复邀约 -8发送短信失败 -18手机号不正确
            "userMobile": "18030985397"
          }
        ]
      }
    }
   */
  const handleImport = useCallback(
    (res: TencentCaptchaResult) => {
      onImport(members, res)
        .then((data) => {
          let currentFailedNum = 0
          if (data.status === 0) {
            const failedMobiles =
              data?.mobiles?.filter((mobile) => mobile?.mobileStatus !== 0) ||
              []
            currentFailedNum = failedMobiles.length
            if (currentFailedNum === 0) {
              onSuccess()
            } else {
              const reasons = handleReasons(failedMobiles)
              Modal.info({
                title: '提示：',
                content: (
                  <div>
                    <p>
                      成功数量
                      <strong>{members.length - currentFailedNum}</strong>
                    </p>
                    <p>
                      失败数量<strong>{currentFailedNum}</strong>
                    </p>
                    <p>失败原因：</p>
                    {reasons.isExist > 0 && <p>部分成员已在团队中</p>}
                    {reasons.isDuplicate > 0 && (
                      <p>部分成员已邀请，请勿重复邀请</p>
                    )}
                    {reasons.sendMsmFail > 0 && <p>部分手机号发送短信失败</p>}
                    {reasons.mobileError > 0 && <p>部分手机号不正确</p>}
                    {reasons.other > 0 && <p>其他原因，请联系客服</p>}
                  </div>
                ),
                onOk: () => {
                  const lines = value.split('\n')
                  const errorMobiles = failedMobiles.map(
                    (mobile) => mobile.userMobile
                  )
                  const errorLines = lines.filter((line) => {
                    const nameAndMobile = parseLineToNameAndMobile(line)
                    return errorMobiles.indexOf(nameAndMobile[1]) > -1
                  })
                  const newErrorUsers = parseLinesToUsers(errorLines)
                  setValue(errorLines.join('\n'))
                  setMembers(newErrorUsers)
                }
              })
            }
          } else {
            Modal.warning({
              title: '邀请失败',
              content: data.msg
            })
          }
          return currentFailedNum
        })
        .then((failedNum) => {
          sensors.track(
            'web_workgroup_membermanagement_addmember_Import_submit',
            {
              members: members.length,
              succeedNum: members.length - failedNum,
              failedNum
            }
          )
        })
    },
    [
      members,
      onImport,
      onSuccess,
      parseLinesToUsers,
      parseLineToNameAndMobile,
      value
    ]
  )

  useEffect(() => {
    const tCaptcha = (res: TencentCaptchaResult) => {
      if (res.ret === 0) {
        handleImport(res)
      }
    }
    if ((window as any).TencentCaptcha === undefined) {
      loadScript('/TCaptcha.js', () => {
        tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
      })
    } else {
      tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
    }
  }, [handleImport])

  const handleSubmitClick = () => {
    if (members.length > 100) {
      Modal.error({ title: '一次最多导入100个成员！' })
      return
    }
    tencentCaptcha?.show()
  }

  return (
    <div className="paste-or-input">
      <div className="paste-wrapper">
        <p>
          请在输入框中按照格式输入成员姓名和手机号，也可以复制后粘贴到输入框中：
        </p>
        <TextArea
          className="paste"
          placeholder="张三&#9;15010101010&#10;李四&#9;13880808080"
          value={value}
          onChange={splitInput}
          autoSize={{ minRows: 10 }}
        />
        <p style={{ width: '480px' }}>
          提示：团队成员之间用换行分隔，成员姓名与手机号用空格分隔，逗号将被替换为空格。
        </p>
      </div>
      <div className="preview-wrapper">
        <p>预览：</p>
        <Table dataSource={members} size="small" pagination={false} bordered>
          <Column
            title="姓名"
            key="userName"
            width="220px"
            dataIndex="userName"
            render={(name) => {
              if (!name) {
                return (
                  <span style={{ color: '#ff7875' }}>请输入正确的姓名</span>
                )
              }
              if (name.length > 10) {
                return (
                  <Tooltip title="姓名最多10个字符！">
                    <span style={{ color: 'red' }}>{name}</span>
                  </Tooltip>
                )
              }
              return name
            }}
          />
          <Column
            title="手机号"
            key="userMobile"
            dataIndex="userMobile"
            render={(mobile) => {
              if (CONFIG.mobileRegex.test(mobile)) {
                return mobile
              } else if (!mobile) {
                return (
                  <span style={{ color: '#ff7875' }}>请输入11位置手机号！</span>
                )
              } else {
                return (
                  <Tooltip title="手机号格式不正确！">
                    <span style={{ color: 'red' }}>{mobile}</span>
                  </Tooltip>
                )
              }
            }}
          />
        </Table>
        {hasError && (
          <p>提示：红色内容表示格式错误，请在左侧输入框中修改相应内容。</p>
        )}
      </div>
      <Divider />
      <Button
        type="primary"
        disabled={members.length === 0 || hasError}
        onClick={handleSubmitClick}
      >
        提交
      </Button>
    </div>
  )
}

const ImportFromExcel: React.FC<ImportFromExcelProps> = ({
  onImport,
  onSuccess,
  onError
}) => {
  const [members, setMembers] = useState<MemberData[]>([])
  const [hasError, setHasError] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const clearPage = useCallback(() => {
    setMembers([])
    setHasError(false)
    setFileList([])
  }, [])

  const getCellPlainValue = (value: any): string => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'object' && value?.richText) {
      const texts = value.richText.map(
        (richText: { text: string }) => richText.text
      )
      return texts.join('')
    }
    return ''
  }

  const parseExcel = async (file: File) => {
    const workbook = new Excel.Workbook()
    const reader = new FileReader()
    reader.onloadend = function () {
      const arrayBuffer = reader.result as ArrayBuffer
      workbook.xlsx.load(arrayBuffer).then(function (wb) {
        const users: MemberData[] = []
        const sheet = wb.getWorksheet(1)
        if (!sheet || sheet.actualRowCount <= 1) {
          Modal.error({
            title: '表格中无数据，请按照模板填写成员信息后提交。',
            onOk: clearPage
          })
        } else if (sheet.actualRowCount > 101) {
          Modal.error({ title: '一次最多导入100个成员！', onOk: clearPage })
        } else {
          wb.worksheets[0].eachRow(function (row, rowNumber) {
            if (rowNumber > 1) {
              let name = row.values[1] ? getCellPlainValue(row.values[1]) : ''
              let mobile = row.values[2] ? getCellPlainValue(row.values[2]) : ''
              name = name.trim().replace(/\n/g, '')
              mobile = mobile
                .substring(mobile.indexOf('1'))
                .trim()
                .replace(/\n/g, '')
              users.push({
                key: rowNumber,
                userName: name,
                userMobile: mobile
              })
              if (
                !name ||
                name.length > 10 ||
                !mobile ||
                !CONFIG.mobileRegex.test(mobile)
              ) {
                setHasError(true)
              }
            }
          })
          setMembers(users)
        }
      })
    }
    reader.readAsArrayBuffer(file)
  }

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isExcelFile =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel'
    if (!isExcelFile) {
      message.error('文件格式错误，仅支持xls、xlsx文件！')
    } else if (file.size / 1024 / 1024 > 1) {
      message.error('上传文件过大，文件最大支持1MB！')
    } else {
      setHasError(false)
      setFileList([file as any])
      setMembers([])
      parseExcel(file as File)
    }
    return false
  }

  const uploadProps: UploadProps = {
    accept:
      'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'file',
    multiple: false,
    beforeUpload,
    fileList
  }

  /**
   *
   * {
      "code": 200,
      "msg": "success",
      "toast_msg": "",
      "data": {
        "status": 0, // 外层状态 0 成功，非0失败，显示msg
        "mobiles": [
          {
            "mobileStatus": 0, // 个人状态 0成功 4已在团队中 7重复邀约 -8发送短信失败 -18手机号不正确
            "userMobile": "18030985397"
          }
        ]
      }
    }
   */
  const handleImport = useCallback(
    (res: TencentCaptchaResult) => {
      onImport(members, res)
        .then((data) => {
          let currentFailedNum = 0
          if (data.status === 0) {
            const failedMobiles =
              data?.mobiles?.filter((mobile) => mobile?.mobileStatus !== 0) ||
              []
            currentFailedNum = failedMobiles.length
            if (currentFailedNum === 0) {
              onSuccess()
            } else {
              const reasons = handleReasons(failedMobiles)
              Modal.confirm({
                title: '提示',
                content: (
                  <div>
                    <p>
                      成功数量
                      <strong>{members.length - currentFailedNum}</strong>
                    </p>
                    <p>
                      失败数量<strong>{currentFailedNum}</strong>
                    </p>
                    <p>失败原因：</p>
                    {reasons.isExist > 0 && <p>部分成员已在团队中</p>}
                    {reasons.isDuplicate > 0 && (
                      <p>部分成员已邀请，请勿重复邀请</p>
                    )}
                    {reasons.sendMsmFail > 0 && <p>部分手机号发送短信失败</p>}
                    {reasons.mobileError > 0 && <p>部分手机号不正确</p>}
                    {reasons.other > 0 && <p>其他原因，请联系客服</p>}
                  </div>
                ),
                okText: '在线修改',
                onOk: () => {
                  sensors.track(
                    'web_workgroup_membermanagement_addmember_Excel_edit',
                    {
                      from: 'excel'
                    }
                  )
                  const errorMobiles = failedMobiles.map(
                    (mobile) => mobile.userMobile
                  )
                  const errorUsersFiltered = members.filter(
                    (member) => errorMobiles.indexOf(member.userMobile) > -1
                  )
                  onError(errorUsersFiltered)
                },
                cancelText: '重新上传',
                onCancel: () => {
                  sensors.track(
                    'web_workgroup_membermanagement_addmember_Excel_resubmit',
                    {
                      from: 'excel'
                    }
                  )
                  clearPage()
                }
              })
            }
          } else {
            Modal.warning({
              title: '邀请失败',
              content: data.msg
            })
          }
          return currentFailedNum
        })
        .then((failedNum) => {
          sensors.track(
            'web_workgroup_membermanagement_addmember_Excel_upload',
            {
              members: members.length,
              succeedNum: members.length - failedNum,
              failedNum,
              from: 'excel'
            }
          )
        })
    },
    [onImport, onSuccess, onError, clearPage, members]
  )

  useEffect(() => {
    const tCaptcha = (res: TencentCaptchaResult) => {
      if (res.ret === 0) {
        handleImport(res)
      }
    }
    if ((window as any).TencentCaptcha === undefined) {
      loadScript('/TCaptcha.js', () => {
        tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
      })
    } else {
      tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
    }
  }, [handleImport])

  const handleSubmitClick = () => {
    tencentCaptcha?.show()
  }

  const downloadExcel = () => {
    sensors.track('web_workgroup_membermanagement_addmember_Excel_download', {
      from: 'excel'
    })
    FileSaver.saveAs(CONFIG.memberExcelUrl, '今日水印相机-通讯录模板.xlsx')
  }

  return (
    <div className="import-from-excel">
      <div className="download-upload">
        <div className="download">
          <p>1.下载模板，填写成员信息</p>
          <div>
            <img src={excelIcon} alt="" />
          </div>
          <Button type="dashed" onClick={downloadExcel}>
            <DownloadOutlined />
          </Button>
        </div>
        <Divider className="divider" orientation="vertical" dashed />
        <div className="upload">
          <p>2.上传填写完毕的Excel文件</p>
          <div>
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                点击选择文件或将文件拖拽到此处上传
              </p>
              <p className="ant-upload-hint">仅支持xls、xlsx格式文件</p>
            </Dragger>
          </div>
        </div>
      </div>
      {members.length > 0 && (
        <div className="preview-wrapper">
          <p>预览：</p>
          <Table dataSource={members} size="small" pagination={false} bordered>
            <Column
              title="姓名"
              key="userName"
              dataIndex="userName"
              render={(name) => {
                if (!name) {
                  return (
                    <span style={{ color: '#ff7875' }}>
                      解析失败，请输入汉字（纯文本格式）！
                    </span>
                  )
                }
                if (name.length > 10) {
                  return (
                    <Tooltip title="姓名最多10个字符！">
                      <span style={{ color: 'red' }}>{name}</span>
                    </Tooltip>
                  )
                }
                return name
              }}
            />
            <Column
              title="手机号"
              key="userMobile"
              dataIndex="userMobile"
              render={(mobile) => {
                if (CONFIG.mobileRegex.test(mobile)) {
                  return mobile
                } else if (!mobile) {
                  return (
                    <span style={{ color: '#ff7875' }}>
                      解析失败，请输入11位置手机号！
                    </span>
                  )
                } else {
                  return (
                    <Tooltip title="手机号格式不正确！">
                      <span style={{ color: 'red' }}>{mobile}</span>
                    </Tooltip>
                  )
                }
              }}
            />
          </Table>
          {hasError && (
            <p>提示：红色文字表示内容错误，请在Excel中修改相应内容后重试。</p>
          )}
        </div>
      )}
      <Divider />
      <Button
        type="primary"
        disabled={members.length === 0 || hasError}
        onClick={handleSubmitClick}
      >
        提交
      </Button>
    </div>
  )
}

function BunchImport() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const groupId = useAppSelector((state) => state.group.groupId)
  const groupSize = useAppSelector((state) => state.group.groupSize)
  const groupSizeLimit = useAppSelector((state) => state.group.groupSizeLimit)
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup)

  const [type, setType] = useState<'excel' | 'input'>(
    parseQueryString().from === 'excel' ? 'excel' : 'input'
  )
  const [errorUsers, setErrorUsers] = useState<MemberData[]>([])

  const handleImport = (
    users: MemberData[],
    res: TencentCaptchaResult
  ): Promise<ImportResponse> => {
    const departmentID = parseQueryString().departmentID
    if ((groupSize ?? 0) + users.length >= (groupSizeLimit ?? Infinity)) {
      Modal.warning({
        title: '邀请成员失败',
        content: `团队成员已达到上限${groupSizeLimit}人，您可以清理已离职的成员，或联系客服咨询商务合作。`
      })
      return Promise.resolve({ status: -1 })
    }
    return dispatch(
      bunchInvite({
        userID: user?.user_id,
        groupID: groupId,
        mobiles: users,
        departmentID: departmentID,
        ticket: res.ticket,
        randStr: res.randstr
      })
    ).catch(capturePromiseException) as Promise<ImportResponse>
  }

  const handleSuccess = () => {
    Modal.info({
      title: '邀请成功',
      content: '已发出短信邀请，对方同意后即可加入团队',
      okText: '知道了',
      onOk: () => {
        dispatch(
          fetchGroupInfo({
            group_id: groupId,
            only_in_group: isOnlyInGroup
          })
        ).catch(capturePromiseException)
        dispatch(fetchGroupDeparment({ groupID: groupId }))
        navigate(-1)
      }
    })
  }

  const handleExcelError = (users: MemberData[]) => {
    setErrorUsers(users)
    setType('input')
  }

  return (
    <Layout className="BunchImport">
      <PageHeader
        className="Header"
        ghost={true}
        title="导入成员"
        subTitle={
          type === 'excel' &&
          '为确保导入成功，请严格按照模板里的格式填写用户信息！'
        }
      />
      <Content className="content">
        {type === 'input' && (
          <ImportFromInput
            errorUsers={errorUsers}
            onImport={handleImport}
            onSuccess={handleSuccess}
          />
        )}
        {type === 'excel' && (
          <ImportFromExcel
            onImport={handleImport}
            onSuccess={handleSuccess}
            onError={handleExcelError}
          />
        )}
      </Content>
    </Layout>
  )
}

export default BunchImport
